import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para Carga (PMC: CTL/ATL/TSB).
 *
 * Sigue el mismo patrón que `lib/workouts.ts` / `lib/strava.ts`:
 * - Schemas Zod source-of-truth para validar respuestas del backend.
 * - Helpers `getJson`/`postJson` propios (no usamos `request` de `./api`)
 *   por consistencia con los demás clientes (cada feature controla su mapeo
 *   de errores; aquí de momento es genérico, pero queda extensible para
 *   añadir códigos específicos cuando aparezcan).
 *
 * Contrato: ver `docs/contracts/M2.1-pmc-load.md`.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/**
 * Estados de forma derivados de TSB (Training Stress Balance):
 * - `fresh`     (tsb >= +25)         → descansado / pico
 * - `fit`       (-10 < tsb < +25)    → en forma, listo
 * - `transition`(-30 < tsb <= -10)   → fatiga acumulada, transición
 * - `overreaching`(tsb <= -30)       → sobrecarga, riesgo
 *
 * El backend devuelve la cadena ya calculada; aquí sólo la tipamos.
 */
export const FORM_STATUSES = ['fresh', 'fit', 'transition', 'overreaching'] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const LoadDaySchema = z.object({
  dateISO: z.string(),
  tss: z.number().nonnegative(),
  ctl: z.number(),
  atl: z.number(),
  tsb: z.number(),
});
export type LoadDay = z.infer<typeof LoadDaySchema>;

export const LoadTimelineResponseSchema = z.object({
  days: z.array(LoadDaySchema),
});
export type LoadTimelineResponse = z.infer<typeof LoadTimelineResponseSchema>;

export const LoadTodayResponseSchema = z.object({
  dateISO: z.string(),
  tss: z.number().nonnegative(),
  ctl: z.number(),
  atl: z.number(),
  tsb: z.number(),
  formStatus: z.enum(FORM_STATUSES),
});
export type LoadTodayResponse = z.infer<typeof LoadTodayResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: zonas y monotonía (M2.6)
// ---------------------------------------------------------------------------

/**
 * Una zona individual dentro de la distribución semanal.
 *
 * `zone` se mantiene como string libre (no enum) porque el backend agrupa
 * por `actual_workouts.intensityZone` tal cual, y puede haber etiquetas
 * mixtas (Z1, Z2, recovery, threshold, vo2max, unzoned…) que el cliente
 * solo necesita pintar con un color por familia. La normalización a color
 * se hace en el componente (`zoneToColor`).
 */
export const ZoneSlotSchema = z.object({
  zone: z.string(),
  minutes: z.number().nonnegative(),
  percent: z.number().min(0).max(100),
});
export type ZoneSlot = z.infer<typeof ZoneSlotSchema>;

export const LoadZonesResponseSchema = z.object({
  weekStartISO: z.string(),
  weekEndISO: z.string(),
  totalMinutes: z.number().nonnegative(),
  zones: z.array(ZoneSlotSchema),
});
export type LoadZonesResponse = z.infer<typeof LoadZonesResponseSchema>;

/**
 * Una semana con sus indicadores de monotonía/strain/rampRate.
 * - `monotony` puede ser `null` si la stddev fue 0 (semana plana sin
 *   variación → indeterminado).
 */
export const WeekMonotonySchema = z.object({
  weekStartISO: z.string(),
  tssTotal: z.number().nonnegative(),
  monotony: z.number().nullable(),
  strain: z.number().nullable(),
  rampRate: z.number(),
});
export type WeekMonotony = z.infer<typeof WeekMonotonySchema>;

export const MonotonyResponseSchema = z.object({
  weeks: z.array(WeekMonotonySchema),
});
export type MonotonyResponse = z.infer<typeof MonotonyResponseSchema>;

export const RecomputeRequestSchema = z
  .object({
    fromISO: z.string().optional(),
    toISO: z.string().optional(),
  })
  .optional();
export type RecomputeRequest = z.infer<typeof RecomputeRequestSchema>;

export const RecomputeResponseSchema = z.object({
  recomputedDays: z.number().int().nonnegative(),
  fromISO: z.string(),
  toISO: z.string(),
  completedAt: z.string(),
});
export type RecomputeResponse = z.infer<typeof RecomputeResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers (mismo patrón que workouts.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

async function postJson<T>(path: string, schema: z.ZodType<T>, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /api/load/timeline?fromISO=...&toISO=...
 *
 * Si se omiten los rangos, el backend devuelve `hoy-90d..hoy+14d`.
 */
export function getLoadTimeline(
  fromISO?: string,
  toISO?: string,
): Promise<LoadTimelineResponse> {
  const params = new URLSearchParams();
  if (fromISO) params.set('fromISO', fromISO);
  if (toISO) params.set('toISO', toISO);
  const qs = params.toString();
  return getJson(
    `/api/load/timeline${qs ? `?${qs}` : ''}`,
    LoadTimelineResponseSchema,
  );
}

export function getLoadToday(): Promise<LoadTodayResponse> {
  return getJson('/api/load/today', LoadTodayResponseSchema);
}

/**
 * GET /api/load/zones?weekStartISO=ISO
 *
 * Si se omite, el backend devuelve la semana actual (lun-dom UTC).
 */
export function getLoadZones(weekStartISO?: string): Promise<LoadZonesResponse> {
  const qs = weekStartISO ? `?weekStartISO=${encodeURIComponent(weekStartISO)}` : '';
  return getJson(`/api/load/zones${qs}`, LoadZonesResponseSchema);
}

/**
 * GET /api/load/monotony?weeks=N
 *
 * Devuelve indicadores de monotonía/strain/rampRate de las últimas N
 * semanas (por defecto 4). El orden cronológico (más antigua primero)
 * lo garantiza el backend.
 */
export function getLoadMonotony(weeks = 4): Promise<MonotonyResponse> {
  return getJson(`/api/load/monotony?weeks=${encodeURIComponent(String(weeks))}`, MonotonyResponseSchema);
}

export function recomputeLoad(body?: RecomputeRequest): Promise<RecomputeResponse> {
  return postJson('/api/load/recompute', RecomputeResponseSchema, body ?? {});
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const LOAD_QUERY_KEY = ['load'] as const;
export const LOAD_TIMELINE_QUERY_KEY = ['load', 'timeline'] as const;
export const LOAD_TODAY_QUERY_KEY = ['load', 'today'] as const;
export const LOAD_ZONES_QUERY_KEY = ['load', 'zones'] as const;
export const LOAD_MONOTONY_QUERY_KEY = ['load', 'monotony'] as const;

export function useLoadTimeline(
  fromISO?: string,
  toISO?: string,
): UseQueryResult<LoadTimelineResponse, Error> {
  return useQuery<LoadTimelineResponse, Error>({
    queryKey: [...LOAD_TIMELINE_QUERY_KEY, fromISO ?? null, toISO ?? null],
    queryFn: () => getLoadTimeline(fromISO, toISO),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useLoadToday(): UseQueryResult<LoadTodayResponse, Error> {
  return useQuery<LoadTodayResponse, Error>({
    queryKey: LOAD_TODAY_QUERY_KEY,
    queryFn: getLoadToday,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useLoadZones(
  weekStartISO?: string,
): UseQueryResult<LoadZonesResponse, Error> {
  return useQuery<LoadZonesResponse, Error>({
    queryKey: [...LOAD_ZONES_QUERY_KEY, weekStartISO ?? null],
    queryFn: () => getLoadZones(weekStartISO),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useLoadMonotony(weeks = 4): UseQueryResult<MonotonyResponse, Error> {
  return useQuery<MonotonyResponse, Error>({
    queryKey: [...LOAD_MONOTONY_QUERY_KEY, weeks],
    queryFn: () => getLoadMonotony(weeks),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Mutación `recomputeLoad`. Tras éxito invalida toda la cache de carga
 * (`['load']`) — coincide con prefijos `['load','timeline']` y `['load','today']`.
 */
export function useRecomputeLoad(): UseMutationResult<
  RecomputeResponse,
  Error,
  RecomputeRequest | undefined
> {
  const qc = useQueryClient();
  return useMutation<RecomputeResponse, Error, RecomputeRequest | undefined>({
    mutationFn: (body) => recomputeLoad(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LOAD_QUERY_KEY });
    },
  });
}
